// 3D Goggle Configurator — Shopify port of the WooCommerce configurator.
// Three.js viewer + tabbed part picker. Adds selected parts as individual
// line items via the Shopify Cart AJAX API.

import * as THREE from 'three';
import { GLTFLoader } from 'three/addons/loaders/GLTFLoader.js';
import { RGBELoader } from 'three/addons/loaders/RGBELoader.js';
import { OrbitControls } from 'three/addons/controls/OrbitControls.js';

const CONFIG = window.configuratorConfig || {};
const I18N = CONFIG.i18n || {};

// ============================================
// State
// ============================================
let scene, camera, renderer, controls;
let loadedModels = { frames: null, lens1: null, lens2: null, strap: null };
let selectedProducts = { frames: null, lens1: null, lens2: null, strap: null };
let currentLensView = 'primary';
let isExploded = false;
let allLensesData = [];
let completeProductsData = [];
let selectedFrameModel = null;
let currentModelPositioning = null;
let isLoadingFromUrl = false;
let modelPositioning = {};

// The GLBs are authored pre-assembled in a shared coordinate space, so every
// part must load at the SAME offset (matching the WordPress configurator, which
// used a uniform {0,0,0.1} per model). Differing per-part offsets pull the
// assembled parts apart. Per-model overrides via custom.model_positioning still
// win when present.
const DEFAULT_POSITIONING = {
  frames: { x: 0, y: 0, z: 0 },
  lens1:  { x: 0, y: 0, z: 0 },
  lens2:  { x: 0, y: 0, z: 0 },
  strap:  { x: 0, y: 0, z: 0 }
};

const EXPLODED_POSITIONS = {
  frames: { x: 0, y: 0, z: 0.05 },
  lens1:  { x: 0, y: 0, z: 0.1 },
  lens2:  { x: 0, y: 0, z: 0.15 },
  strap:  { x: 0, y: 0, z: 0 }
};

// Load product data emitted by the section
const configuratorData = JSON.parse(document.getElementById('configurator-data').textContent);
allLensesData = configuratorData.lenses || [];
completeProductsData = configuratorData.complete_products || [];

// Build the positioning map keyed by frame model attribute
(configuratorData.frames || []).forEach((frame) => {
  if (frame.model_attribute && frame.positioning && !modelPositioning[frame.model_attribute]) {
    modelPositioning[frame.model_attribute] = frame.positioning;
  }
});

console.log('✓ THREE.js', THREE.REVISION, '| frames:', (configuratorData.frames || []).length,
  '| lenses:', allLensesData.length, '| straps:', (configuratorData.strap || []).length);

// ============================================
// Card rendering
// ============================================
function createProductCard(product, category) {
  const card = document.createElement('div');
  card.className = 'product-card';
  card.setAttribute('data-product-id', product.id);
  card.setAttribute('data-variant-id', product.variant_id || '');
  card.setAttribute('data-product-category', category);
  card.setAttribute('data-product-name', product.name);
  card.setAttribute('data-product-price', product.price);
  card.setAttribute('data-in-stock', product.in_stock ? 'true' : 'false');
  card.setAttribute('data-model-url', product.model_url || '');
  card.setAttribute('data-frame-code', product.frame_code || '');
  card.setAttribute('data-lens-code', product.lens_code || '');
  card.setAttribute('data-frame-hex', product.frame_hex || '');
  card.setAttribute('data-lens-hex', product.lens_hex || '');
  if (product.model_attribute) card.setAttribute('data-model-attribute', product.model_attribute);
  card.addEventListener('click', () => selectProduct(card));

  if (selectedProducts[category] && String(selectedProducts[category].id) === String(product.id)) {
    card.classList.add('selected');
  }

  const stockBadge = product.in_stock
    ? '<span class="stock-badge in-stock">In Stock</span>'
    : '<span class="stock-badge out-of-stock">Out of Stock</span>';
  card.innerHTML = stockBadge;

  if (product.product_content && product.product_content.trim() !== '') {
    const infoBtn = document.createElement('button');
    infoBtn.className = 'product-info-btn';
    infoBtn.addEventListener('click', (e) => {
      e.stopPropagation();
      openProductInfoModal(product.name, product.product_content);
    });
    infoBtn.innerHTML = '<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><circle cx="12" cy="12" r="10"></circle><line x1="12" y1="16" x2="12" y2="12"></line><line x1="12" y1="8" x2="12.01" y2="8"></line></svg>';
    card.appendChild(infoBtn);
  }

  if (product.image) {
    const img = document.createElement('img');
    img.className = 'product-image';
    img.src = product.image;
    img.alt = product.name;
    img.loading = 'lazy';
    card.appendChild(img);
  }

  const info = document.createElement('div');
  info.className = 'product-info';
  const title = document.createElement('div');
  title.className = 'product-title';
  title.textContent = product.name;
  const price = document.createElement('div');
  price.className = 'product-price';
  price.innerHTML = product.price;
  info.appendChild(title);
  info.appendChild(price);
  if (product.sku) {
    const sku = document.createElement('div');
    sku.className = 'product-sku';
    sku.textContent = (I18N.sku || 'SKU:') + ' ' + product.sku;
    info.appendChild(sku);
  }
  card.appendChild(info);
  return card;
}

function renderGrid(gridId, products, category) {
  const grid = document.getElementById(gridId);
  if (!grid) return;
  grid.innerHTML = '';
  if (!products || products.length === 0) {
    grid.innerHTML = '<div class="empty-state" style="width:100%"><p>No products available</p></div>';
    return;
  }
  products.forEach((p) => grid.appendChild(createProductCard(p, category)));
}

// ============================================
// Lens filtering by frame model
// ============================================
function updateLensOptions() {
  if (!selectedFrameModel) return;
  const compatible = allLensesData.filter((l) => l.model_attribute === selectedFrameModel);

  renderLensGrid('lens1', compatible);
  renderLensGrid('lens2', compatible);

  const has = compatible.length > 0;
  // Only the primary lens tab unlocks when a frame is chosen. The secondary
  // lens + straps stay locked until the (required) primary lens is selected —
  // see updateTabAvailability().
  document.getElementById('btn-lens1').disabled = !has;

  toggleEl('lens1-disabled', !has);
  toggleEl('lens1-grid', has, 'flex');
  toggleEl('lens2-disabled', !has);
  toggleEl('lens2-grid', has, 'flex');
}

function renderLensGrid(lensType, lenses) {
  const grid = document.getElementById(lensType + '-grid');
  if (!grid) return;
  grid.innerHTML = '';
  if (lenses.length === 0) {
    grid.innerHTML = '<div class="empty-state" style="width:100%"><p>No compatible lenses found for this frame</p></div>';
    return;
  }
  lenses.forEach((lens) => grid.appendChild(createProductCard(lens, lensType)));
}

function toggleEl(id, show, display = 'block') {
  const el = document.getElementById(id);
  if (el) el.style.display = show ? display : 'none';
}

function clearLensSelections() {
  selectedProducts.lens1 = null;
  selectedProducts.lens2 = null;
  ['lens1', 'lens2'].forEach((c) => {
    if (loadedModels[c]) { scene.remove(loadedModels[c]); loadedModels[c] = null; }
  });
  document.querySelectorAll('#lens1-grid .product-card, #lens2-grid .product-card')
    .forEach((c) => c.classList.remove('selected'));
}

// ============================================
// Selection handler (with deselect)
// ============================================
function selectProduct(element) {
  const category = element.getAttribute('data-product-category');
  const productId = element.getAttribute('data-product-id');
  const variantId = element.getAttribute('data-variant-id');
  const productName = element.getAttribute('data-product-name');
  const modelUrl = element.getAttribute('data-model-url');
  const modelAttribute = element.getAttribute('data-model-attribute');
  const frameCode = element.getAttribute('data-frame-code');
  const lensCode = element.getAttribute('data-lens-code');
  const frameHex = element.getAttribute('data-frame-hex');
  const lensHex = element.getAttribute('data-lens-hex');
  const inStock = element.getAttribute('data-in-stock') === 'true';

  const isSelected = element.classList.contains('selected');

  if (isSelected) {
    element.classList.remove('selected');
    selectedProducts[category] = null;
    if (loadedModels[category]) { scene.remove(loadedModels[category]); loadedModels[category] = null; }

    if (category === 'frames') {
      selectedFrameModel = null;
      currentModelPositioning = null;
      clearLensSelections();
      document.getElementById('btn-lens1').disabled = true;
      document.getElementById('btn-lens2').disabled = true;
      document.getElementById('lens1-grid').innerHTML = '';
      document.getElementById('lens2-grid').innerHTML = '';
      toggleEl('lens1-disabled', true); toggleEl('lens1-grid', false);
      toggleEl('lens2-disabled', true); toggleEl('lens2-grid', false);
      switchTab('frames');
    }
  } else {
    element.parentElement.querySelectorAll('.product-card').forEach((c) => c.classList.remove('selected'));
    element.classList.add('selected');

    selectedProducts[category] = {
      id: productId, variantId, name: productName, modelUrl, inStock,
      modelAttribute, frameCode, lensCode, frameHex, lensHex
    };

    if (category === 'frames') {
      selectedFrameModel = modelAttribute;
      currentModelPositioning = modelPositioning[selectedFrameModel] || DEFAULT_POSITIONING;
      if (!isLoadingFromUrl) clearLensSelections();
      resetToAssembledState();
      if (loadedModels.strap && currentModelPositioning.strap) {
        animateModelPosition(loadedModels.strap, currentModelPositioning.strap, 500);
      }
      updateLensOptions();
      if (!isLoadingFromUrl) setTimeout(() => switchTab('lens1'), 300);
    }

    if (category === 'lens1' && !isLoadingFromUrl) setTimeout(() => switchTab('lens2'), 300);

    if (category === 'lens2') {
      setTimeout(() => switchLensView('secondary'), 100);
      if (!isLoadingFromUrl) setTimeout(() => switchTab('strap'), 300);
    }

    if (scene && modelUrl) loadModel(modelUrl, category);
  }

  updateSelectionSummary();
  updateAddToCartButton();
  updateTabAvailability();
  hideNotification();
  updateCanvasPlaceholder();
}

// ============================================
// Tabs
// ============================================
function switchTab(tabName) {
  const tabButton = document.querySelector(`[data-tab="${tabName}"]`);
  if (tabButton && tabButton.disabled) return;
  document.querySelectorAll('.tab-button').forEach((b) => b.classList.remove('active'));
  if (tabButton) tabButton.classList.add('active');
  document.querySelectorAll('.tab-content').forEach((c) => c.classList.remove('active'));
  const active = document.getElementById(`tab-${tabName}`);
  if (active) active.classList.add('active');
}

// ============================================
// Summary / button / warnings
// ============================================
function updateSelectionSummary() {
  setText('selected-frame', selectedProducts.frames, I18N.notSelected || 'Not selected');
  setText('selected-lens1', selectedProducts.lens1, I18N.notSelected || 'Not selected');
  setText('selected-lens2', selectedProducts.lens2, I18N.optional || 'Optional');
  setText('selected-strap', selectedProducts.strap, I18N.optional || 'Optional');
}

function setText(id, sel, fallback) {
  const el = document.getElementById(id);
  if (!el) return;
  if (sel) { el.textContent = sel.name; el.classList.remove('not-selected'); }
  else { el.textContent = fallback; el.classList.add('not-selected'); }
}

function updateWarningMessage() {
  const el = document.getElementById('warning-message');
  const text = document.getElementById('warning-text');
  if (!el || !text) return;
  const warnings = [];
  if (selectedProducts.frames && selectedProducts.lens1) {
    if (!selectedProducts.lens2) warnings.push(I18N.considerSecondary || 'Consider adding a secondary lens for different weather conditions');
    if (!selectedProducts.strap) warnings.push(I18N.considerStrap || 'Consider adding a strap to complete your goggle');
  }
  if (warnings.length) { text.textContent = warnings.join(' and '); el.classList.add('show'); }
  else el.classList.remove('show');
}

function updateAddToCartButton() {
  const btn = document.getElementById('add-to-cart-btn');
  if (btn) btn.disabled = !(selectedProducts.frames && selectedProducts.lens1);
  updateWarningMessage();
}

// Step gating: the secondary lens and straps are optional, but they only become
// available once the required primary lens has been selected.
function updateTabAvailability() {
  const hasPrimary = !!selectedProducts.lens1;
  const lens2 = document.getElementById('btn-lens2');
  const strap = document.getElementById('btn-strap');
  if (lens2) lens2.disabled = !hasPrimary;
  if (strap) strap.disabled = !hasPrimary;
}

function updateCanvasPlaceholder() {
  const ph = document.getElementById('canvas-placeholder');
  const any = selectedProducts.frames || selectedProducts.lens1 || selectedProducts.lens2 || selectedProducts.strap;
  if (ph) ph.style.display = any ? 'none' : 'block';
}

// ============================================
// Add to cart — substitute a matching complete goggle where possible
// ============================================
function matchingCompleteProduct() {
  const frame = selectedProducts.frames;
  const primary = selectedProducts.lens1;
  if (!frame || !primary || !frame.modelAttribute) return null;

  // Raw Exact codes are authoritative. Existing colour metafields provide a
  // migration fallback so the feature works before the first forced import.
  const selectedFrameKey = frame.frameCode || frame.frameHex;
  const selectedLensKey = primary.lensCode || primary.lensHex;
  if (!selectedFrameKey || !selectedLensKey) return null;

  const match = completeProductsData.find((product) =>
    product.model_attribute === frame.modelAttribute &&
    (product.frame_code || product.frame_hex) === selectedFrameKey &&
    (product.lens_code || product.lens_hex) === selectedLensKey
  );
  if (!match) return null;
  return {
    ...match,
    variantId: match.variant_id,
    inStock: match.in_stock,
    modelAttribute: match.model_attribute,
    frameCode: match.frame_code,
    lensCode: match.lens_code,
    frameHex: match.frame_hex,
    lensHex: match.lens_hex,
    includedLensCode: match.included_lens_code
  };
}

async function addToCart() {
  if (!selectedProducts.frames || !selectedProducts.lens1) {
    let msg;
    if (!selectedProducts.frames && !selectedProducts.lens1) msg = I18N.selectFrameAndLens || 'Please select a frame and primary lens first';
    else if (!selectedProducts.frames) msg = I18N.selectFrame || 'Please select a frame first';
    else msg = I18N.selectPrimaryLens || 'Please select a primary lens first';
    showNotification(msg, true);
    return;
  }

  // Shopify's multi-item /cart/add is all-or-nothing: one sold-out part makes
  // the whole request fail with 422 and NOTHING gets added. Catch it up front
  // with a clear message instead.
  const completeProduct = matchingCompleteProduct();
  // Exact marks whether this complete SKU includes a secondary lens. Before
  // the first code backfill, support the verified legacy Orion rule through
  // the existing colour metafields: non-photochromatic Orion sets include YLW.
  const includedLensKey = completeProduct?.includedLensCode ||
    (completeProduct?.modelAttribute === 'MOR' && completeProduct.lensHex !== '#d3bdfc'
      ? '#e5de20'
      : '');
  const selectedSecondaryKey = selectedProducts.lens2?.lensCode || selectedProducts.lens2?.lensHex;
  const includedSecondary = !!includedLensKey && selectedSecondaryKey === includedLensKey;
  const cartProducts = completeProduct
    ? [completeProduct, includedSecondary ? null : selectedProducts.lens2, selectedProducts.strap]
    : ['frames', 'lens1', 'lens2', 'strap'].map((c) => selectedProducts[c]);

  const soldOut = cartProducts
    .filter((p) => p && !p.inStock)
    .map((p) => p.name);
  if (soldOut.length > 0) {
    showNotification(soldOut.join(', ') + ' — ' + (I18N.soldOutSuffix || 'sold out — please pick a different part'), true);
    return;
  }

  const items = [];
  cartProducts.forEach((p) => {
    if (p && p.variantId) items.push({ id: Number(p.variantId), quantity: 1 });
  });

  if (items.length === 0) {
    showNotification(I18N.noVariant || 'Selected items have no purchasable variant', true);
    return;
  }

  const btn = document.getElementById('add-to-cart-btn');
  const original = btn.textContent;
  btn.textContent = I18N.adding || 'Adding to cart...';
  btn.disabled = true;

  try {
    const res = await fetch(CONFIG.cartAddUrl || '/cart/add.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
      body: JSON.stringify({ items })
    });
    const data = await res.json();
    btn.textContent = original;
    btn.disabled = false;

    if (res.ok) {
      showNotification('✓ ' + items.length + ' item(s) added to cart');
      document.dispatchEvent(new CustomEvent('configurator:added', { detail: data }));
      setTimeout(() => showModal(I18N.addedTitle || 'Added to cart!', I18N.addedMessage || 'Would you like to go to your cart now?', CONFIG.cartUrl || '/cart'), 600);
    } else {
      // /cart/add reports problems as `description`, `message` or `errors`
      // (string or field map) depending on the failure.
      let msg = data.description || data.message || data.errors || I18N.errorGeneric || 'Could not add to cart';
      if (typeof msg === 'object') msg = Object.values(msg).flat().join(', ');
      showNotification('Error: ' + msg, true);
    }
  } catch (err) {
    console.error(err);
    btn.textContent = original;
    btn.disabled = false;
    showNotification(I18N.errorGeneric || 'Error adding to cart. Please try again.', true);
  }
}

// ============================================
// Notifications + modals
// ============================================
function showNotification(message, isError = false) {
  const n = document.getElementById('configurator-notification');
  if (!n) return;
  n.textContent = message;
  n.classList.add('show');
  n.classList.toggle('error', isError);
  setTimeout(() => n.classList.remove('show'), 4000);
}
function hideNotification() {
  const n = document.getElementById('configurator-notification');
  if (n) n.classList.remove('show');
}

let cartUrl = '';
function showModal(title, message, url) {
  cartUrl = url;
  document.getElementById('modal-title').textContent = title;
  document.getElementById('modal-message').textContent = message;
  document.getElementById('cart-modal').classList.add('show');
}
function openProductInfoModal(name, content) {
  const modal = document.getElementById('product-info-modal');
  document.getElementById('product-info-modal-title').textContent = name;
  document.getElementById('product-info-modal-content').innerHTML = content;
  modal.classList.add('show');
  document.body.style.overflow = 'hidden';
}
function closeProductInfoModal() {
  document.getElementById('product-info-modal').classList.remove('show');
  document.body.style.overflow = '';
}

// ============================================
// Explode + lens view
// ============================================
function toggleExplodeView() {
  isExploded = !isExploded;
  const btn = document.getElementById('explode-toggle');
  btn.classList.toggle('active', isExploded);
  if (isExploded) animateToExploded(); else animateToAssembled();
}
function animateToExploded() {
  Object.keys(loadedModels).forEach((c) => {
    const m = loadedModels[c];
    if (m && EXPLODED_POSITIONS[c]) {
      if (c === 'lens1' || c === 'lens2') m.visible = true;
      animateModelPosition(m, EXPLODED_POSITIONS[c], 500);
    }
  });
}
function animateToAssembled() {
  if (!currentModelPositioning) return;
  Object.keys(loadedModels).forEach((c) => {
    const m = loadedModels[c];
    if (m && currentModelPositioning[c]) {
      animateModelPosition(m, currentModelPositioning[c], 500);
      if (c === 'lens1') m.visible = (currentLensView === 'primary');
      else if (c === 'lens2') m.visible = (currentLensView === 'secondary');
    }
  });
}
function resetToAssembledState() {
  if (!isExploded) return;
  isExploded = false;
  const btn = document.getElementById('explode-toggle');
  if (btn) btn.classList.remove('active');
  animateToAssembled();
}
function animateModelPosition(model, target, duration) {
  const start = { x: model.position.x, y: model.position.y, z: model.position.z };
  const startTime = performance.now();
  function step(now) {
    const p = Math.min((now - startTime) / duration, 1);
    const e = p < 0.5 ? 2 * p * p : 1 - Math.pow(-2 * p + 2, 2) / 2;
    model.position.x = start.x + (target.x - start.x) * e;
    model.position.y = start.y + (target.y - start.y) * e;
    model.position.z = start.z + (target.z - start.z) * e;
    if (p < 1) requestAnimationFrame(step);
  }
  requestAnimationFrame(step);
}
function switchLensView(view) {
  if (isExploded) return;
  currentLensView = view;
  document.querySelectorAll('.lens-switch-btn').forEach((b) => b.classList.remove('active'));
  document.getElementById('lens-switch-' + view).classList.add('active');
  if (loadedModels.lens1 && loadedModels.lens2) {
    loadedModels.lens1.visible = (view === 'primary');
    loadedModels.lens2.visible = (view === 'secondary');
  }
}

// ============================================
// Three.js
// ============================================
function initThreeJS() {
  const container = document.getElementById('threejs-viewer');
  const spinner = document.getElementById('model-loading-spinner');
  if (!container) return;

  scene = new THREE.Scene();
  scene.background = new THREE.Color(0xf0f0f0);

  const w = container.clientWidth, h = container.clientHeight;
  camera = new THREE.PerspectiveCamera(45, w / h, 0.1, 1000);
  camera.position.set(0, 0, 3);

  renderer = new THREE.WebGLRenderer({ antialias: true, alpha: true });
  renderer.setSize(w, h);
  renderer.setPixelRatio(Math.min(window.devicePixelRatio, 2));
  renderer.outputColorSpace = THREE.SRGBColorSpace;
  renderer.toneMapping = THREE.ACESFilmicToneMapping;
  renderer.toneMappingExposure = 1;
  renderer.shadowMap.enabled = true;

  const placeholder = container.querySelector('.viewer-placeholder');
  const viewerControls = container.querySelector('.viewer-controls');
  const infoIcon = container.querySelector('.configurator-info-icon');
  container.innerHTML = '';
  if (placeholder) container.appendChild(placeholder);
  if (viewerControls) container.appendChild(viewerControls);
  container.appendChild(renderer.domElement);
  if (spinner) container.appendChild(spinner);
  if (infoIcon) container.appendChild(infoIcon);

  controls = new OrbitControls(camera, renderer.domElement);
  controls.enableDamping = true;
  controls.dampingFactor = 0.05;
  controls.minDistance = 0.8;
  controls.maxDistance = 3;
  controls.target.set(0, 0, 0);
  controls.update();

  setupLighting();

  if (CONFIG.hdrUrl) {
    new RGBELoader().load(CONFIG.hdrUrl, (texture) => {
      texture.mapping = THREE.EquirectangularReflectionMapping;
      scene.environment = texture;
    }, undefined, (e) => console.warn('HDR failed:', e));
  }

  window.addEventListener('resize', () => {
    const cw = container.clientWidth, ch = container.clientHeight;
    camera.aspect = cw / ch;
    camera.updateProjectionMatrix();
    renderer.setSize(cw, ch);
  });

  (function animate() {
    requestAnimationFrame(animate);
    controls.update();
    renderer.render(scene, camera);
  })();

  setTimeout(loadFromUrlParams, 500);
}

function setupLighting() {
  scene.children.filter((c) => c.isLight).forEach((l) => scene.remove(l));
  scene.add(new THREE.AmbientLight(0xffffff, 0.6));
  const main = new THREE.DirectionalLight(0xffffff, 1.2);
  main.position.set(5, 5, 5); main.castShadow = true; scene.add(main);
  const fill = new THREE.DirectionalLight(0xffffff, 0.4); fill.position.set(-5, 0, 5); scene.add(fill);
  const back = new THREE.DirectionalLight(0xffffff, 0.3); back.position.set(0, 5, -5); scene.add(back);
}

function loadModel(url, category) {
  if (!url || !scene) return;
  const spinner = document.getElementById('model-loading-spinner');
  if (spinner) spinner.style.display = 'flex';

  new GLTFLoader().load(url, (gltf) => {
    if (loadedModels[category]) scene.remove(loadedModels[category]);
    const model = gltf.scene;
    model.traverse((c) => { if (c.isMesh) { c.castShadow = true; c.receiveShadow = true; } });

    let pos;
    if (isExploded) {
      pos = EXPLODED_POSITIONS[category];
      if (category === 'lens1' || category === 'lens2') model.visible = true;
    } else {
      pos = (currentModelPositioning && currentModelPositioning[category]) || DEFAULT_POSITIONING[category];
    }
    if (pos) model.position.set(pos.x, pos.y, pos.z);

    if (!isExploded) {
      if (category === 'lens1') model.visible = (currentLensView === 'primary');
      else if (category === 'lens2') model.visible = (currentLensView === 'secondary');
    }

    scene.add(model);
    loadedModels[category] = model;

    if (Object.values(loadedModels).filter(Boolean).length === 1) {
      const box = new THREE.Box3().setFromObject(model);
      const center = box.getCenter(new THREE.Vector3());
      const size = box.getSize(new THREE.Vector3());
      const maxDim = Math.max(size.x, size.y, size.z);
      const fov = camera.fov * (Math.PI / 180);
      const camZ = Math.abs(maxDim / 2 / Math.tan(fov / 2));
      camera.position.set(center.x, center.y, center.z + camZ);
      controls.target.copy(center);
      controls.update();
    }
    if (spinner) spinner.style.display = 'none';
  }, undefined, (err) => {
    console.error(`Error loading ${category}:`, err);
    if (spinner) spinner.style.display = 'none';
  });
}

// ============================================
// URL params (deep links): ?goggle=&lens1=&lens2=&strap=
// ============================================
function loadFromUrlParams() {
  const p = new URLSearchParams(window.location.search);
  const goggleId = p.get('goggle'), lens1Id = p.get('lens1'), lens2Id = p.get('lens2'), strapId = p.get('strap');
  if (!goggleId && !lens1Id && !lens2Id && !strapId) return;

  const pick = (id, category, delay = 0) => new Promise((resolve) => setTimeout(() => {
    const card = document.querySelector(`.product-card[data-product-id="${id}"][data-product-category="${category}"]`);
    if (card) { selectProduct(card); resolve(true); } else resolve(false);
  }, delay));

  (async () => {
    isLoadingFromUrl = true;
    if (goggleId) {
      const ok = await pick(goggleId, 'frames', 100);
      if (ok) {
        await new Promise((r) => setTimeout(r, 500));
        if (lens1Id) await pick(lens1Id, 'lens1', 200);
        if (lens2Id) await pick(lens2Id, 'lens2', 200);
      }
    }
    if (strapId) await pick(strapId, 'strap', 200);
    isLoadingFromUrl = false;
  })();
}

// ============================================
// Wire up events + boot
// ============================================
function bindEvents() {
  document.getElementById('add-to-cart-btn').addEventListener('click', addToCart);
  const explodeBtn = document.getElementById('explode-toggle');
  if (explodeBtn) explodeBtn.addEventListener('click', toggleExplodeView);
  document.querySelectorAll('.lens-switch-btn').forEach((b) =>
    b.addEventListener('click', () => switchLensView(b.getAttribute('data-lens-view'))));
  document.querySelectorAll('.tab-button').forEach((b) =>
    b.addEventListener('click', () => switchTab(b.getAttribute('data-tab'))));
  document.getElementById('modal-continue').addEventListener('click', () =>
    document.getElementById('cart-modal').classList.remove('show'));
  document.getElementById('modal-gotocart').addEventListener('click', () => { if (cartUrl) window.location.href = cartUrl; });
  document.getElementById('product-info-close').addEventListener('click', closeProductInfoModal);

  document.addEventListener('click', (e) => {
    if (e.target === document.getElementById('cart-modal')) document.getElementById('cart-modal').classList.remove('show');
    if (e.target === document.getElementById('product-info-modal')) closeProductInfoModal();
  });
  document.addEventListener('keydown', (e) => {
    if (e.key === 'Escape') {
      document.getElementById('cart-modal').classList.remove('show');
      closeProductInfoModal();
    }
  });
}

function boot() {
  renderGrid('frames-grid', configuratorData.frames, 'frames');
  renderGrid('strap-grid', configuratorData.strap, 'strap');
  bindEvents();
  initThreeJS();
}

// es-module-shims re-executes this module when it falls back to polyfill mode
// (any other module on the page failing to resolve triggers that), which would
// double-bind every event handler and spin up a second Three.js scene.
if (!window.__aphexConfiguratorBooted) {
  window.__aphexConfiguratorBooted = true;
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }
}
